export class ApiHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail: string,
  ) {
    super(`${status} ${code}: ${detail}`);
    this.name = 'ApiHttpError';
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  /** Convenience: serialised as JSON and Content-Type set automatically. */
  json?: unknown;
  /** Raw body — use `json` instead when sending JSON. */
  body?: BodyInit;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { json, headers, body, ...rest } = options;
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    body: json !== undefined ? JSON.stringify(json) : body,
  };
  const url = `/api${path}`;
  const res = await fetch(url, init);

  if (!res.ok) {
    let code = 'unknown';
    let detail = res.statusText || `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: { error?: string; detail?: string } };
      if (body?.detail && typeof body.detail === 'object') {
        if (body.detail.error) code = body.detail.error;
        if (body.detail.detail) detail = body.detail.detail;
      }
    } catch {
      // non-JSON body, keep defaults
    }
    throw new ApiHttpError(res.status, code, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
